'use client'

import { useState, useEffect } from "react";
import { z } from "zod";
import { ArrowLeft, Smartphone, FileText } from "lucide-react";
import qrCreate from "@utils/qrCreate";
import ModalError from "@components/main/ModalError";
import { Button, Input, Modal, ModalBody, ModalContent, ModalHeader, Image, useDisclosure } from "@heroui/react";

const APP_URL = "https://vcentreitbot.vcentre.pro/?source=pwa";

const formSchema = z.object({
  name: z.string().trim().min(2, "Введите имя").max(100),
  phone: z.string().trim().min(5, "Введите телефон").max(30),
});

type View = "choose" | "form" | "qr";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerLabel?: string;
  initialView?: View;
  title?: string;
}

export const DemoRequestDialog = ({ open, onOpenChange, initialView = "choose", title = "Запросить демонстрацию" }: Props) => {
  const [view, setView] = useState<View>(initialView);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setView(initialView);
  }, [open, initialView]);

  const reset = () => {
    setView(initialView);
    setName("");
    setPhone("");
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = formSchema.safeParse({ name, phone });
    if (!parsed.success) {
        setErrorMessage(`Проверьте данные.\n${parsed.error.issues[0].message}`);
        return;
    }
    setSubmitting(true);
    try {
      const response = await fetch("/api/signup/demo/request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...parsed.data,
          title,
          createdAt: new Date().toISOString(),
        }),
      });
      const result = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(result?.error || "Не удалось отправить заявку");
      }

      setErrorMessage(`Заявка отправлена.\nМы свяжемся с вами в ближайшее время.`);
      handleClose(false);
    } catch (error: any) {
      setErrorMessage(error?.message || "Не удалось отправить заявку. Попробуйте ещё раз.");
    } finally {
      setSubmitting(false);
    }
  };

  const [errorMessage, setErrorMessage] = useState("")
  const [qrFiles, setQrFiles] = useState([] as any)
  const { isOpen: showQr, onOpen: openQr, onOpenChange: setShowQr } = useDisclosure()
  useEffect(() => {
        const fetchGet = async () => {
            try {
                const qrFile = await qrCreate(APP_URL, '')
                setQrFiles([qrFile])
            } catch (error) {
                setErrorMessage(JSON.stringify(error))
            }
        }
        fetchGet()
    }, [])

  return (
    <>
        <ModalError errorMessage={errorMessage} setErrorMessage={setErrorMessage}></ModalError>
        <Modal size="lg"
            isOpen={open} 
            placement={'center'}
            onOpenChange={onOpenChange} 
            className="block-active !bg-[var(--color-main2)]"
            onClose={() => {if(setErrorMessage) setErrorMessage('')}}
        >
            <ModalContent className="max-w-md">
                {view === "choose" && (
                    <>
                        <ModalHeader className="flex flex-col">
                            <p className="headerh4">{title}</p>
                            <p className="input-label">
                                Выберите удобный способ — оставьте заявку или попробуйте приложение прямо сейчас.
                            </p>
                        </ModalHeader>
                        <ModalBody>
                            <div className="grid gap-3 mt-2">
                                <button
                                    onClick={() => setView("form")}
                                    className="block-active text-left p-5 hover:border-primary transition-colors flex items-start gap-4"
                                >
                                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <FileText className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <div className="font-montserrat-semibold text-foreground mb-1">Оставить заявку</div>
                                        <div className="text-sm text-muted-foreground font-montserrat">Имя и телефон — мы свяжемся с вами</div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => setView("qr")}
                                    className="block-active text-left p-5 hover:border-primary transition-colors flex items-start gap-4"
                                >
                                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <Smartphone className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <div className="font-montserrat-semibold text-foreground mb-1">Перейти в приложение</div>
                                        <div className="text-sm text-muted-foreground font-montserrat">QR-код или кнопка для открытия</div>
                                    </div>
                                </button>
                            </div>
                        </ModalBody>
                    </>
                )}

                {view === "form" && (
                <>
                    <ModalHeader className="flex flex-col">
                        <button onClick={() => setView("choose")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2 font-montserrat">
                            <ArrowLeft className="w-4 h-4" /> Назад
                        </button>
                        <p className="headerh4">Оставить заявку</p>
                        <p className="input-label">Мы свяжемся в течение рабочего дня.</p>
                    </ModalHeader>
                    <ModalBody>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Input
                                    label="Имя"
                                    id="demo-name"
                                    variant="underlined"
                                    classNames={{
                                        base: 'input-base w-full !m-0 !p-0',
                                        label: 'input-label',
                                        inputWrapper: 'input-input',
                                        helperWrapper: 'input-label'
                                    }}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Ваше имя"
                                    maxLength={100}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Input
                                    label="Телефон"
                                    id="demo-phone"
                                    type="tel"
                                    value={phone}
                                    variant="underlined"
                                    classNames={{
                                        base: 'input-base w-full !m-0 !p-0',
                                        label: 'input-label',
                                        inputWrapper: 'input-input',
                                        helperWrapper: 'input-label'
                                    }}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="+7 ___ ___ __ __"
                                    maxLength={30}
                                    required
                                />
                            </div>
                            <button
                                type="submit" disabled={submitting} 
                                className="button-border my-2 mx-auto w-1/2 place-content-center"
                                
                            >
                                {submitting ? "Отправка..." : "Отправить заявку"}
                            </button>
                        </form>
                    </ModalBody>
                </>
                )}

                {view === "qr" && (
                <>
                    <ModalHeader className="flex flex-col">
                        <button onClick={() => setView("choose")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2 font-montserrat">
                            <ArrowLeft className="w-4 h-4" /> Назад
                        </button>
                        <p className="headerh4">Перейти в приложение</p>
                        <p className="input-label">Отсканируйте QR-код с телефона или откройте приложение здесь.</p>
                    </ModalHeader>
                    <div className="flex flex-col items-center gap-6 mt-2">
                        <div className="p-4 bg-white rounded-xl border border-border">
                            <Image
                                src={qrFiles[0]}
                                width={200}
                                height={200}
                                radius='none'
                                alt="QR Telegram"
                                className="cursor-zoom-in rounded"
                                onClick={openQr}
                            />
                        </div>
                        <a href={APP_URL} target="_blank" rel="noopener noreferrer" className="button-brand inline-flex items-center gap-2 w-full justify-center">
                            Открыть приложение
                        </a>
                    </div>
                </>
                )}
            </ModalContent>
        </Modal>
    </>
  );
};
